import { useEffect, useState } from 'preact/hooks';
import type { UnitMode } from '../../shared/recipe';
import type { Me } from './api';

// ---------------------------------------------------------------- tiny observable store

export function createStore<T>(initial: T) {
  let value = initial;
  const subs = new Set<(v: T) => void>();
  return {
    get: () => value,
    set(next: T | ((v: T) => T)) {
      value = typeof next === 'function' ? (next as (v: T) => T)(value) : next;
      subs.forEach((s) => s(value));
    },
    use(): T {
      const [v, setV] = useState(value);
      useEffect(() => {
        subs.add(setV);
        setV(value);
        return () => void subs.delete(setV);
      }, []);
      return v;
    },
  };
}

// ---------------------------------------------------------------- who is looking

/** The current visitor; `authenticated` means the owner, signed in for admin changes. */
export const session = createStore<Me | null>(null);
export const useIsAdmin = (): boolean => !!session.use()?.authenticated;

// ---------------------------------------------------------------- per-device preferences

export interface Prefs {
  units: UnitMode;
  density: 'comfortable' | 'compact' | 'list';
  theme: 'dark' | 'light';
  cookTheme: 'dark' | 'light';
}

const PREFS_KEY = 'bakery.prefs';
const defaults: Prefs = { units: 'metric', density: 'comfortable', theme: 'dark', cookTheme: 'light' };

function loadPrefs(): Prefs {
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') };
  } catch {
    return defaults;
  }
}

export const prefs = createStore<Prefs>(loadPrefs());

export function setPref<K extends keyof Prefs>(k: K, v: Prefs[K]) {
  prefs.set((p) => ({ ...p, [k]: v }));
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs.get()));
  } catch {
    /* private mode */
  }
}

// ---------------------------------------------------------------- toasts

export interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'error';
}

export const toasts = createStore<Toast[]>([]);
let toastId = 0;
export function toast(text: string, kind: Toast['kind'] = 'info') {
  const id = ++toastId;
  toasts.set((t) => [...t, { id, text, kind }]);
  setTimeout(() => toasts.set((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 7000 : 3500);
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ---------------------------------------------------------------- data hook

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | undefined; error: unknown; loading: boolean; reload: () => void; setData: (d: T) => void } {
  const [state, setState] = useState<{ data: T | undefined; error: unknown; loading: boolean }>({ data: undefined, error: null, loading: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fn().then(
      (data) => alive && setState({ data, error: null, loading: false }),
      (error) => alive && setState({ data: undefined, error, loading: false }),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return { ...state, reload: () => setN((x) => x + 1), setData: (d: T) => setState({ data: d, error: null, loading: false }) };
}
